import { Box3, type Frustum, Matrix4, Vector3 } from 'three'

export type TilesBoundingVolume =
  | { readonly type: 'box'; readonly values: readonly number[] }
  | { readonly type: 'sphere'; readonly values: readonly number[] }
  | { readonly type: 'region'; readonly values: readonly number[] }

export type TilesCoordinateOptions =
  | {
      readonly mode: 'local'
      readonly upAxis?: 'X' | 'Y' | 'Z'
      readonly unit?: 'meter'
      readonly metersPerUnit?: number
    }
  | { readonly mode: 'ecef'; readonly anchor: readonly [number, number, number] }

export type TilesSourceBounds = {
  readonly min: readonly [number, number, number]
  readonly max: readonly [number, number, number]
}

export type TilesReference = { sourceToRecord: Matrix4; recordToSource: Matrix4 }

const WGS84_A = 6378137
const WGS84_E2 = 6.6943799901413165e-3
const ROUNDING_MARGIN = 64 * Number.EPSILON

function finiteArray(value: unknown, length: number, label: string): number[] {
  if (
    !Array.isArray(value) ||
    value.length !== length ||
    !Array.from(value).every(Number.isFinite)
  ) {
    throw new Error(`${label} 必须包含 ${length} 个有限数值`)
  }
  return [...value]
}

/** 只解析空间形状；来源及诊断阶段由输入层补充。 */
export function parseTilesBoundingVolume(input: unknown): TilesBoundingVolume {
  if (!input || typeof input !== 'object') throw new Error('boundingVolume 必须为对象')
  const source = input as Record<string, unknown>
  const kinds = (['box', 'sphere', 'region'] as const).filter((key) => key in source)
  if (kinds.length !== 1) throw new Error('boundingVolume 必须且只能指定一种形状')
  const type = kinds[0]
  const values = finiteArray(source[type], type === 'box' ? 12 : type === 'sphere' ? 4 : 6, type)
  if (type === 'sphere' && values[3] < 0) throw new Error('sphere 半径不能为负')
  if (type === 'region') {
    const [west, south, east, north, minHeight, maxHeight] = values
    if (
      Math.abs(west) > Math.PI ||
      Math.abs(east) > Math.PI ||
      south < -Math.PI / 2 ||
      north > Math.PI / 2 ||
      south > north ||
      minHeight > maxHeight
    ) {
      throw new Error('region 经纬度或高度范围无效')
    }
  }
  return Object.freeze({ type, values: Object.freeze(values) })
}

/** 宿主和 tile 矩阵必须为有限仿射矩阵；允许退化轴，但不支持投影变换。 */
export function validateTilesTransform(matrix: Matrix4): void {
  const e = matrix.elements
  if (!e.every(Number.isFinite) || e[3] !== 0 || e[7] !== 0 || e[11] !== 0 || e[15] !== 1) {
    throw new Error('Tiles transform 必须为有限仿射矩阵')
  }
}

/** glTF 完整场景的 Y-up → tile Z-up，只在内容链应用一次。 */
export function createGltfToTilesMatrix(): Matrix4 {
  return new Matrix4().set(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1)
}

/** ECEF 锚点决定固定 ENU；源与 Record 之间的逆映射不含宿主放置。 */
export function createTilesReference(options: TilesCoordinateOptions): TilesReference {
  let sourceToRecord: Matrix4
  if (options.mode === 'local') {
    const scale = options.metersPerUnit ?? 1
    const axis = options.upAxis ?? 'Z'
    if (
      !Number.isFinite(scale) ||
      scale <= 0 ||
      (options.unit !== undefined && options.unit !== 'meter') ||
      !['X', 'Y', 'Z'].includes(axis)
    ) {
      throw new Error('local 轴向或米制单位比例无效')
    }
    sourceToRecord =
      axis === 'Y'
        ? createGltfToTilesMatrix()
        : axis === 'X'
          ? new Matrix4().set(0, 0, -1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1)
          : new Matrix4()
    sourceToRecord.scale(new Vector3(scale, scale, scale))
  } else if (options.mode === 'ecef') {
    const [x, y, z] = finiteArray(options.anchor, 3, 'ECEF anchor')
    if (Math.hypot(x, y, z) < 1) throw new Error('ECEF anchor 不能位于地心')
    const p = Math.hypot(x, y)
    const longitude = Math.atan2(y, x)
    let latitude = Math.atan2(z, p * (1 - WGS84_E2))
    // 固定点反解椭球纬度；极轴也自然收敛，避免除以 cos(latitude)。
    for (let i = 0; i < 32; i++) {
      const sin = Math.sin(latitude)
      const n = WGS84_A / Math.sqrt(1 - WGS84_E2 * sin * sin)
      const next = Math.atan2(z + WGS84_E2 * n * sin, p)
      if (Math.abs(next - latitude) < 1e-14) break
      latitude = next
    }
    const sl = Math.sin(longitude)
    const cl = Math.cos(longitude)
    const sp = Math.sin(latitude)
    const cp = Math.cos(latitude)
    sourceToRecord = new Matrix4()
      .set(-sl, cl, 0, 0, -sp * cl, -sp * sl, cp, 0, cp * cl, cp * sl, sp, 0, 0, 0, 0, 1)
      .multiply(new Matrix4().makeTranslation(-x, -y, -z))
  } else {
    throw new Error('不支持的 Tiles 坐标参考')
  }
  const recordToSource = sourceToRecord.clone().invert()
  // Three 的通用求逆会让齐次常量因舍入成为 0.9999999999999999；这里已知为仿射。
  recordToSource.elements[3] = 0
  recordToSource.elements[7] = 0
  recordToSource.elements[11] = 0
  recordToSource.elements[15] = 1
  validateTilesTransform(sourceToRecord)
  validateTilesTransform(recordToSource)
  if (sourceToRecord.determinant() === 0) throw new Error('源参考映射不可逆')
  return { sourceToRecord, recordToSource }
}

/** 归一化 A 后用 AᵀA 的最大绝对行和界，剪切/负缩放不会低估最大奇异值。 */
export function getMaximumStretch(matrix: Matrix4): number {
  validateTilesTransform(matrix)
  const e = matrix.elements
  const columns = [
    new Vector3(e[0], e[1], e[2]),
    new Vector3(e[4], e[5], e[6]),
    new Vector3(e[8], e[9], e[10]),
  ]
  const scale = Math.max(...columns.flatMap((v) => [Math.abs(v.x), Math.abs(v.y), Math.abs(v.z)]))
  if (scale === 0) return 0
  for (const column of columns) column.set(column.x / scale, column.y / scale, column.z / scale)
  let rowBound = 0
  let trace = 0
  for (let i = 0; i < 3; i++) {
    trace += columns[i].lengthSq()
    rowBound = Math.max(
      rowBound,
      columns.reduce((sum, column) => sum + Math.abs(columns[i].dot(column)), 0)
    )
  }
  return scale * Math.sqrt(Math.min(trace, rowBound)) * (1 + ROUNDING_MARGIN)
}

type Interval = readonly [number, number]

function product(a: Interval, b: Interval): Interval {
  const values = [a[0] * b[0], a[0] * b[1], a[1] * b[0], a[1] * b[1]]
  return [Math.min(...values), Math.max(...values)]
}

function sineRange(start: number, end: number): Interval {
  let min = Math.min(Math.sin(start), Math.sin(end))
  let max = Math.max(Math.sin(start), Math.sin(end))
  for (let k = Math.ceil((start - Math.PI / 2) / Math.PI); Math.PI / 2 + k * Math.PI <= end; k++) {
    if (k % 2 === 0) max = 1
    else min = -1
  }
  return [min, max]
}

/** 区间算术包含曲面内部极值、跨反经线和任意高度端点，不只取八个角点。 */
export function getRegionEcefBounds(values: readonly number[]): Box3 {
  const region = parseTilesBoundingVolume({ region: values }).values
  const [west, south, east, north, hMin, hMax] = region
  const end = east < west ? east + 2 * Math.PI : east
  const sinLat = sineRange(south, north)
  const cosLat = sineRange(south + Math.PI / 2, north + Math.PI / 2)
  const minSinSq = south <= 0 && north >= 0 ? 0 : Math.min(sinLat[0] ** 2, sinLat[1] ** 2)
  const maxSinSq = Math.max(sinLat[0] ** 2, sinLat[1] ** 2)
  const nMin = WGS84_A / Math.sqrt(1 - WGS84_E2 * minSinSq)
  const nMax = WGS84_A / Math.sqrt(1 - WGS84_E2 * maxSinSq)
  const radial = product([nMin + hMin, nMax + hMax], cosLat)
  const x = product(radial, sineRange(west + Math.PI / 2, end + Math.PI / 2))
  const y = product(radial, sineRange(west, end))
  const z = product([nMin * (1 - WGS84_E2) + hMin, nMax * (1 - WGS84_E2) + hMax], sinLat)
  const box = new Box3(new Vector3(x[0], y[0], z[0]), new Vector3(x[1], y[1], z[1]))
  const magnitude = Math.max(
    WGS84_A,
    ...box.min.toArray().map(Math.abs),
    ...box.max.toArray().map(Math.abs)
  )
  return box.expandByScalar(magnitude * ROUNDING_MARGIN)
}

export type OrientedBounds = { center: Vector3; axes: Vector3[]; sphere: boolean }

export function prepareTilesBounds(
  bounds: TilesBoundingVolume,
  tile: Matrix4,
  reference: Matrix4,
  host: Matrix4
): OrientedBounds {
  validateTilesTransform(tile)
  validateTilesTransform(reference)
  validateTilesTransform(host)
  const matrix = host.clone().multiply(reference)
  if (bounds.type !== 'region') matrix.multiply(tile)
  validateTilesTransform(matrix)
  let center: Vector3
  let axes: Vector3[]
  if (bounds.type === 'region') {
    const box = getRegionEcefBounds(bounds.values)
    center = box.getCenter(new Vector3())
    const half = box.getSize(new Vector3()).multiplyScalar(0.5)
    axes = [new Vector3(half.x, 0, 0), new Vector3(0, half.y, 0), new Vector3(0, 0, half.z)]
  } else {
    const values = bounds.values
    center = new Vector3(values[0], values[1], values[2])
    axes =
      bounds.type === 'box'
        ? [
            new Vector3().fromArray(values, 3),
            new Vector3().fromArray(values, 6),
            new Vector3().fromArray(values, 9),
          ]
        : [new Vector3(values[3], 0, 0), new Vector3(0, values[3], 0), new Vector3(0, 0, values[3])]
  }
  const e = matrix.elements
  for (const axis of axes) {
    const { x, y, z } = axis
    axis.set(
      e[0] * x + e[4] * y + e[8] * z,
      e[1] * x + e[5] * y + e[9] * z,
      e[2] * x + e[6] * y + e[10] * z
    )
  }
  return { center: center.applyMatrix4(matrix), axes, sphere: bounds.type === 'sphere' }
}

function support(shape: OrientedBounds, direction: Vector3): number {
  const dots = shape.axes.map((axis) => Math.abs(axis.dot(direction)))
  return shape.sphere ? Math.hypot(...dots) : dots.reduce((a, b) => a + b, 0)
}

function shapeBox(shape: OrientedBounds): Box3 {
  const half = new Vector3(
    support(shape, new Vector3(1, 0, 0)),
    support(shape, new Vector3(0, 1, 0)),
    support(shape, new Vector3(0, 0, 1))
  )
  return new Box3(shape.center.clone().sub(half), shape.center.clone().add(half))
}

/** 不传 host 时返回 Record-local 范围，可交给 SceneBounds 应用宿主矩阵一次。 */
export function getTilesBoundingBox(
  bounds: TilesBoundingVolume,
  tile: Matrix4,
  reference: Matrix4,
  host = new Matrix4()
): Box3 {
  return shapeBox(prepareTilesBounds(bounds, tile, reference, host))
}

export function intersectsTilesBounds(
  bounds: TilesBoundingVolume,
  frustum: Frustum,
  tile: Matrix4,
  reference: Matrix4,
  host = new Matrix4()
): boolean {
  return intersectsPreparedTilesBounds(prepareTilesBounds(bounds, tile, reference, host), frustum)
}

/** 消费本次选择准备的形状；不修改或持有该形状。 */
export function intersectsPreparedTilesBounds(shape: OrientedBounds, frustum: Frustum): boolean {
  return frustum.planes.every((plane) => {
    const radius = support(shape, plane.normal)
    const margin = (shape.center.length() + radius + Math.abs(plane.constant) + 1) * ROUNDING_MARGIN
    return plane.distanceToPoint(shape.center) + radius >= -margin
  })
}

/** 返回距离下界用于保守 SSE；剪切 box / 椭球不假定正交，不会因高估距离漏细化。 */
export function distanceToTilesBounds(
  bounds: TilesBoundingVolume,
  point: Vector3,
  tile: Matrix4,
  reference: Matrix4,
  host = new Matrix4()
): number {
  return distanceToPreparedTilesBounds(prepareTilesBounds(bounds, tile, reference, host), point)
}

/** 与声明包围体路径共用距离下界算法。 */
export function distanceToPreparedTilesBounds(shape: OrientedBounds, point: Vector3): number {
  const delta = point.clone().sub(shape.center)
  let distance = shapeBox(shape).distanceToPoint(point)
  const directions = [delta.clone().normalize()]
  if (!shape.sphere) {
    for (let i = 0; i < 3; i++)
      directions.push(
        new Vector3().crossVectors(shape.axes[i], shape.axes[(i + 1) % 3]).normalize()
      )
  }
  for (const direction of directions)
    distance = Math.max(distance, Math.abs(delta.dot(direction)) - support(shape, direction))
  return Math.max(0, distance - (point.length() + shape.center.length() + 1) * ROUNDING_MARGIN)
}

/** 已含 tile/content 变换的审计源 AABB；不接收 tile 或宿主矩阵。 */
export function validateSourceBounds(input: TilesSourceBounds, reference = new Matrix4()): Box3 {
  const min = finiteArray(input?.min, 3, 'sourceBounds.min')
  const max = finiteArray(input?.max, 3, 'sourceBounds.max')
  if (min.some((value, i) => value > max[i])) throw new Error('sourceBounds min 不能超过 max')
  validateTilesTransform(reference)
  return new Box3(new Vector3().fromArray(min), new Vector3().fromArray(max)).applyMatrix4(
    reference
  )
}

/** error 已包含一次累积 tile 伸缩、参考单位与宿主伸缩；投影矩阵已包含 zoom。 */
export function computeTilesSSE(
  error: number,
  distance: number,
  projection: Matrix4,
  cssHeight: number,
  perspective: boolean
): number {
  if (
    ![error, distance, cssHeight].every(Number.isFinite) ||
    error < 0 ||
    distance < 0 ||
    cssHeight < 0 ||
    !projection.elements.every(Number.isFinite)
  ) {
    throw new Error('SSE 输入必须为有限非负数与有效投影矩阵')
  }
  if (error === 0 || cssHeight === 0) return 0
  const pixelsPerUnit = (cssHeight * Math.abs(projection.elements[5])) / 2
  return perspective
    ? distance === 0
      ? Infinity
      : (error * pixelsPerUnit) / distance
    : error * pixelsPerUnit
}
